/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { patientApi } from '../lib/api';
import { patientStorage } from '../lib/patientStorage';
import { capturePatientSession } from '../lib/api/sessionToken';
import { PatientPortalProfile } from '../types';
import { clearPdfCache } from '../lib/pdfCache';
import { useAccountLanguageSync } from '../i18n/useAccountLanguageSync';

interface PatientAuthContextType {
  patient: PatientPortalProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  /** Adopts a session another sign-in route already established (mobile + SMS code). */
  adoptSession: (response: unknown) => void;
  signOut: () => void;
  refresh: () => Promise<void>;
}

const PatientAuthContext = createContext<PatientAuthContextType | undefined>(undefined);

// Mirrors AuthContext's staff-side silent-refresh timer — see that file for the full rationale.
const REFRESH_BUFFER_MS = 2 * 60 * 1000;
const MIN_REFRESH_DELAY_MS = 30 * 1000;
const MAX_REFRESH_DELAY_MS = 6 * 60 * 60 * 1000;
/** When the remaining lifetime is unknown (a page load on an existing session), refresh this soon. */
const UNKNOWN_LIFETIME_REFRESH_MS = 10 * 60 * 1000;

export function PatientAuthProvider({ children }: { children: React.ReactNode }) {
  const [patient, setPatient] = useState<PatientPortalProfile | null>(null);
  // The portal language follows the account across devices and is saved when changed (medopd-docs 106).
  useAccountLanguageSync(patient?.id ?? null, patient?.preferredLanguage, (lng) => patientApi.auth.setMyLanguage(lng));
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Silently extends the patient session before the access token expires, via the
   * patient_refresh_token httpOnly cookie — mirrors AuthContext.scheduleTokenRefresh on the staff
   * side. The token itself is an HttpOnly cookie the page cannot read (medopd-docs 107), so the
   * timer runs off the lifetime the server reports (expiresInSeconds), or a short default when unknown.
   */
  const scheduleTokenRefresh = useCallback((lifetimeSeconds: number | null | undefined) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const rawDelay = lifetimeSeconds && lifetimeSeconds > 0
      ? lifetimeSeconds * 1000 - REFRESH_BUFFER_MS
      : UNKNOWN_LIFETIME_REFRESH_MS;
    const delay = Math.min(Math.max(rawDelay, MIN_REFRESH_DELAY_MS), MAX_REFRESH_DELAY_MS);

    refreshTimerRef.current = setTimeout(async () => {
      try {
        const response = await patientApi.auth.refresh();
        if (response.error) return; // fetchPatientApi's own 401 handling covers redirect-to-login
        const lifetime = capturePatientSession(response);
        if (lifetime !== null) scheduleTokenRefresh(lifetime);
      } catch {
        // Refresh failed — let the existing 401-on-next-request handling take over.
      }
    }, delay);
  }, []);

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const initialize = async () => {
      if (patientStorage.hasActiveSession()) {
        try {
          const response = await patientApi.auth.getCurrentPatient();
          const responseData = (response.data as { data?: { patient?: PatientPortalProfile } } | undefined)?.data || response.data;
          if (responseData?.patient) {
            setPatient(responseData.patient);
            scheduleTokenRefresh(null);
          } else {
            patientStorage.clearSession();
          }
        } catch {
          patientStorage.clearSession();
        }
      }
      setLoading(false);
    };

    initialize();
  }, [scheduleTokenRefresh]);

  const signIn = async (email: string, password: string) => {
    const response = await patientApi.auth.login(email, password);
    if (response.error) {
      throw new Error(response.error);
    }

    const responseData = (response.data as { data?: { patient?: PatientPortalProfile } } | undefined)?.data || response.data;
    const lifetime = capturePatientSession(response);
    if (lifetime === null || !responseData?.patient) {
      throw new Error('Invalid response from server');
    }

    setPatient(responseData.patient);
    scheduleTokenRefresh(lifetime);
    syncProfile();
  };

  /**
   * The sign-in response carries a basic profile; /me carries the rest, including whether the current
   * Agreement still needs accepting. Without this the portal's terms gate was skipped right after
   * signing in and only appeared on the next page load. Best effort: the basic profile stands if it fails.
   */
  const syncProfile = () => {
    patientApi.auth.getCurrentPatient()
      .then((response) => {
        const data = (response?.data as { data?: { patient?: PatientPortalProfile } } | undefined)?.data || response?.data;
        if (!response?.error && data?.patient) setPatient(data.patient);
      })
      .catch(() => undefined);
  };

  const adoptSession = (response: unknown) => {
    const responseData = (response as { data?: { patient?: PatientPortalProfile } } | undefined)?.data;
    const lifetime = capturePatientSession(response);
    if (lifetime === null || !responseData?.patient) throw new Error('Invalid response from server');
    setPatient(responseData.patient);
    scheduleTokenRefresh(lifetime);
    syncProfile();
  };

  const signOut = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    patientStorage.clearSession();
    clearPdfCache(); // reports opened in this tab must not survive into another patient's session
    // Don't let a clinic-switch choice leak into the next login on a shared device.
    sessionStorage.removeItem('active_patient_clinic_id');
    sessionStorage.removeItem('active_dependent_patient_id');
    setPatient(null);
    // The session is an HttpOnly cookie only the server can end (medopd-docs 107), so wait for logout — up to a
    // few seconds — before leaving: navigating away first could cancel the request and leave the cookie live.
    const leave = () => { window.location.href = '/patient/login'; };
    Promise.race([
      patientApi.auth.logout().catch(() => undefined),
      new Promise((resolve) => setTimeout(resolve, 3000)),
    ]).then(leave, leave);
  };

  const refresh = async () => {
    const response = await patientApi.auth.getCurrentPatient();
    if (response.error) {
      throw new Error(response.error);
    }

    const responseData = (response.data as { data?: { patient?: PatientPortalProfile } } | undefined)?.data || response.data;
    if (responseData?.patient) {
      setPatient(responseData.patient);
    }
  };

  return (
    <PatientAuthContext.Provider value={{ patient, loading, signIn, adoptSession, signOut, refresh }}>
      {children}
    </PatientAuthContext.Provider>
  );
}

export function usePatientAuth() {
  const context = useContext(PatientAuthContext);
  if (!context) {
    throw new Error('usePatientAuth must be used within PatientAuthProvider');
  }
  return context;
}
